import TextStyleToolbar from '../story/TextStyleToolbar';
import { TextStyleFields } from '../story/types';
import React, { useEffect, useMemo, useRef, useState } from 'react';

interface CaptionWord {
  text: string;
  start: number;
  end: number;
}

interface Caption {
  id: string;
  text: string;
  start_time: number;
  end_time: number;
  style: Record<string, any>;
  words?: CaptionWord[];
}

interface CaptionPanelProps {
  captions: Caption[];
  onCaptionsChange: (captions: Caption[]) => void;
  onRegenerate: () => void;
  currentTime: number;
  onSeek?: (time: number) => void;
}

const formatTime = (t: number) => {
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
};

const CaptionPanel: React.FC<CaptionPanelProps> = ({ captions, onCaptionsChange, onRegenerate, currentTime, onSeek }) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [autoFollow, setAutoFollow] = useState(true);
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);

  const listRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const activeCaption = useMemo(
    () => captions.find(c => currentTime >= c.start_time && currentTime <= c.end_time) || null,
    [captions, currentTime]
  );

  useEffect(() => {
    if (!autoFollow || !activeCaption) return;
    itemRefs.current[activeCaption.id]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [autoFollow, activeCaption]);

  useEffect(() => {
    if (!actionsMenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (!(e.target as Element).closest('.caption-menu')) setActionsMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [actionsMenuOpen]);

  const startEdit = (cap: Caption) => {
    setEditingId(cap.id);
    setEditText(cap.text);
  };

  const saveEdit = (id: string) => {
    onCaptionsChange(captions.map(c => c.id === id ? { ...c, text: editText } : c));
    setEditingId(null);
  };

  const deleteCaption = (id: string) => {
    onCaptionsChange(captions.filter(c => c.id !== id));
  };

  const addCaption = () => {
    const newCap: Caption = {
      id: `new-${Date.now()}`,
      text: 'New caption',
      start_time: currentTime,
      end_time: currentTime + 2,
      style: {},
    };
    onCaptionsChange([...captions, newCap]);
    startEdit(newCap);
  };

  const updateTiming = (id: string, field: 'start_time' | 'end_time', value: number) => {
    onCaptionsChange(captions.map(c => c.id === id ? { ...c, [field]: value } : c));
  };

  /** Caption styling matches the Story and Kinetic Caption toolbars — size is a % of frame height. */
  const updateStyle = (id: string, patch: Partial<TextStyleFields>) => {
    onCaptionsChange(captions.map(c =>
      c.id === id ? { ...c, style: { ...(c.style || {}), ...patch } } : c));
  };

  const seekTo = (t: number) => onSeek?.(t);

  const filtered = search.trim()
    ? captions.filter(c => c.text.toLowerCase().includes(search.trim().toLowerCase()))
    : captions;

  return (
    <div className="caption-panel">
      <div className="caption-panel-header">
        <h3 className="panel-title">Captions</h3>
        {!autoFollow && (
          <button className="caption-action-btn caption-action-btn--ghost caption-jump-btn" onClick={() => setAutoFollow(true)}>
            ⤓ Jump to current
          </button>
        )}
        <input
          type="text"
          className="caption-search-input"
          placeholder="Search transcript…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <div className="caption-menu">
          <button
            className="project-card-menu kc-render-menu-trigger"
            onClick={() => setActionsMenuOpen(v => !v)}
          >
            ⋯
          </button>
          {actionsMenuOpen && (
            <div className="project-card-dropdown">
              <button onClick={() => { onRegenerate(); setActionsMenuOpen(false); }}>🔄 Regenerate</button>
              <button onClick={() => { addCaption(); setActionsMenuOpen(false); }}>+ Add Caption</button>
            </div>
          )}
        </div>
      </div>

      <div
        className="caption-list"
        ref={listRef}
        onWheel={() => setAutoFollow(false)}
        onTouchMove={() => setAutoFollow(false)}
      >
        {filtered.length === 0 ? (
          <div className="caption-empty">
            <p>{search ? 'No captions match your search.' : 'No captions yet.'}</p>
            {!search && <button className="caption-action-btn caption-action-btn--accent" onClick={addCaption}>+ Add Caption</button>}
          </div>
        ) : (
          filtered.map(cap => {
            const isActive = currentTime >= cap.start_time && currentTime <= cap.end_time;
            const isExpanded = expandedId === cap.id;
            const hasWords = !!cap.words && cap.words.length > 0;
            return (
              <div
                key={cap.id}
                ref={el => { itemRefs.current[cap.id] = el; }}
                className={`caption-item ${isActive ? 'caption-item--active' : ''}`}
              >
                {editingId === cap.id ? (
                  <div className="caption-edit">
                    <div className="caption-timing" onClick={e => e.stopPropagation()}>
                      <input
                        type="number" step="0.1" min="0"
                        value={cap.start_time.toFixed(1)}
                        onChange={e => updateTiming(cap.id, 'start_time', parseFloat(e.target.value) || 0)}
                        className="time-input"
                      />
                      <span>–</span>
                      <input
                        type="number" step="0.1" min="0"
                        value={cap.end_time.toFixed(1)}
                        onChange={e => updateTiming(cap.id, 'end_time', parseFloat(e.target.value) || 0)}
                        className="time-input"
                      />
                    </div>
                    <textarea
                      value={editText}
                      onChange={e => setEditText(e.target.value)}
                      maxLength={140}
                      autoFocus
                    />
                    <div className="caption-style-row" onClick={e => e.stopPropagation()}>
                      <TextStyleToolbar
                        fields={(cap.style || {}) as TextStyleFields}
                        onChange={patch => updateStyle(cap.id, patch)}
                      />
                    </div>
                    <div className="caption-edit-actions">
                      <button className="btn-sm btn-accent" onClick={() => saveEdit(cap.id)} title="Save this caption's text, timing and style">Save</button>
                      <button className="btn-sm btn-ghost" onClick={() => setEditingId(null)} title="Discard changes to this caption">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div className="caption-text" onClick={() => seekTo(cap.start_time)}>
                    <div className="caption-timing" onClick={e => e.stopPropagation()}>
                      <input
                        type="number" step="0.1" min="0"
                        value={cap.start_time.toFixed(1)}
                        onChange={e => updateTiming(cap.id, 'start_time', parseFloat(e.target.value) || 0)}
                        className="time-input"
                      />
                      <span>–</span>
                      <input
                        type="number" step="0.1" min="0"
                        value={cap.end_time.toFixed(1)}
                        onChange={e => updateTiming(cap.id, 'end_time', parseFloat(e.target.value) || 0)}
                        className="time-input"
                      />
                    </div>
                    <span className="caption-text-content">{cap.text}</span>
                    <div className="caption-item-actions">
                      {hasWords && (
                        <button
                          className="btn-icon"
                          onClick={(e) => { e.stopPropagation(); setExpandedId(isExpanded ? null : cap.id); }}
                          title={isExpanded ? 'Hide words' : `Show ${cap.words!.length} words`}
                        >
                          {isExpanded ? '▾' : '▸'}
                        </button>
                      )}
                      <button className="btn-icon" onClick={(e) => { e.stopPropagation(); startEdit(cap); }} title="Edit">✏️</button>
                      <button className="btn-icon" onClick={(e) => { e.stopPropagation(); deleteCaption(cap.id); }} title="Delete">🗑️</button>
                    </div>
                  </div>
                )}
                {isExpanded && hasWords && (
                  <div className="caption-word-list">
                    {cap.words!.map((w, i) => (
                      <button
                        key={i}
                        className="caption-word-chip"
                        onClick={(e) => { e.stopPropagation(); seekTo(w.start); }}
                        title={`${formatTime(w.start)} – ${formatTime(w.end)}`}
                      >
                        {w.text}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default CaptionPanel;
